import { ref, watch, computed, onMounted, onUnmounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';

import useLoadingStates from '@/hooks/loading/useLoadingStates.js';
import { useIntersectionObserver } from '@/hooks/browserAPI/IntersectionObserver';
import { 
  HEADER_IO_OPTIONS,
  HEADER_IO_TOGGLER,
  BOOK_ENTITY_IO_OPTIONS
} from '@/hooks/browserAPI/IntersectionObserver';
import useWindow from '@/hooks/browserAPI/Window';
import { fetchStreamData } from '@/utils/loaders';
import { TaskReaderClient } from '@/generated/taskreader/taskreader_grpc_web_pb.js';
import { useRemoteTitleDescription } from '@/utils/seo';
import { openPublicFile } from '@/utils/links.js';
import { extractTokenInfo } from '@/utils/auth';

import { UrlError } from './errors';


const SUBJECT_LIMIT = 3;
const LEVEL_LIMIT = 7;
const GENERAL_LIMIT = 9000;
const SERVICE_URL = process.env.VUE_APP_REGISTRATION_SERVICE_URL;
const EMPTY_BOOKS_LIMIT = 7;
const EMPTY_BOOKS_LIMIT_NARROW = 4;
const BOOK_CARD_WIDTH = 160;

const MODE = Object.freeze({
  LEVEL: 0,
  SUBJECT: 1,
  BOTH: 2,
  UNDEFINED: 3,
})


export default function useBooksList(opts = { mode: MODE.UNDEFINED }) {
  const route = useRoute();
  const router = useRouter();
  const store = useStore();
  const { LOADING_STATES } = useLoadingStates();

  // Set intersection observer for header
  const { 
    observedElement: contentHeader, 
    startObservation: observeHeader
  } = useIntersectionObserver({
    options: HEADER_IO_OPTIONS,
    callback: HEADER_IO_TOGGLER('content__header_sticky')
  });


  // Set intersection observer for navigation
  const entityIntersections = ref({});
  function entityIOHandler(intersections) {
    return (entries) => {
      entries.forEach(entry => {
        if (entry.intersectionRatio > 0) {
          // Part of viewport is more robust since it isnt related with entity size
          const k = entry.intersectionRect.height / entry.rootBounds.height;
          intersections.value[entry.target.id] = k;
        } else {
          if (intersections.value.hasOwnProperty(entry.target.id)) {
            delete intersections.value[entry.target.id];
          }
        }
      });
    }
  }
  const { startMultipleObservation: observeBookEntities } = useIntersectionObserver({
    options: BOOK_ENTITY_IO_OPTIONS,
    callback: entityIOHandler(entityIntersections)
  });


  // Shared reactive variables
  const currentMode =  ref(opts.mode);
  const subjectName = ref(null);
  const levelName = ref(null);
  const subject = ref(null);
  const level = ref(null);
  const isLoading = ref(true);
  const books = ref([]);
  const bookEntities = ref(null);
  const bookEntitiesRefs = ref({});
  const selectedEntityIds = ref(null);
  const emptyBooksNumber = ref(EMPTY_BOOKS_LIMIT_NARROW);
  const emptyBooksNumberLimit = ref(EMPTY_BOOKS_LIMIT_NARROW);
  const { screenWidth } = useWindow();


  // Refs: this works for vue before 3.5 versions
  const contentContainer = ref(null);


  // Watchers
  watch(
    () => entityIntersections.value,
    (newIntersections) => {
      let maxIntersectionRatio = -1;
      for (const ratio of Object.values(newIntersections)) {
        if (ratio > maxIntersectionRatio) {
          maxIntersectionRatio = ratio;
        }
      }
      const bestEntityIds = [];
      for (const [id, ratio] of Object.entries(newIntersections)) {
        if (ratio === maxIntersectionRatio) {
          bestEntityIds.push(id);
        }
      }
      selectedEntityIds.value = new Set(bestEntityIds);
    },
    { immediate: true, deep: true }
  );


  // Computed functions
  const getGrades = computed(() => store.getters['gradeData/getGrades']);
  const getCourses = computed(() => store.getters['courseData/getCourses']);
  const getGradeState = computed(() => store.getters['gradeData/getState']);
  const getCourseState = computed(() => store.getters['courseData/getState']);
  const getSubjectsToIds = computed(() => store.getters['coursesMapping/getSubjectsToIds']);
  const getLevelsToIds = computed(() => store.getters['coursesMapping/getLevelsToIds']);
  const getIdsToSubjects = computed(() => store.getters['coursesMapping/getIdsToSubjects']);
  const getIdsToLevels = computed(() => store.getters['coursesMapping/getIdsToLevels']);

  const loadingStatesFull = computed(() => {
    // const gradeState = getGradeState.value;
    // const courseState = getCourseState.value;
    // const routeParams = route.params;
    return {
      gradeState: getGradeState.value,
      courseState: getCourseState.value,
      routeParams: route.params
    }
});

  const getStatesForBooks = computed(() => {
    if (getGradeState.value !== LOADING_STATES.LOADED
      || getCourseState.value !== LOADING_STATES.LOADED
    ) { return null; }
    let subjTitle = getCourses.value.find(sub => sub.id === +subject.value)?.title || null;
    let lvlTitle = getGrades.value.find(lvl => lvl.id === +level.value)?.title || null;
    if (subjTitle || lvlTitle) {
      return { subjTitle, lvlTitle };
    }
  });

  const updateBreadcrumbLinks = computed(() => {
    const homeLink = { id: 1, path: { name: 'tasks' }, title: 'Решения' };
    const titles = getStatesForBooks.value;
    if (!titles) {
      return [homeLink];
    }
    let id = 0;
    const { subjTitle, lvlTitle } = titles;
    const links = [homeLink];
    const levelLink = {
      id,
      path: { 
        name: 'level-books',
        params: { level: levelName.value }
      },
      title: '',
      disabled: false
    };
    const subjectLink = {
      id: ++id,
      path: { 
        name: 'subject-books',
        params: { subject: subjectName.value }
      },
      title: '',
      disabled: true
    };
    
    try {
      switch (currentMode.value) {
        case MODE.BOTH:
          levelLink.title = lvlTitle;
          subjectLink.title = subjTitle;
          links.push(levelLink);
          links.push(subjectLink);
          break;
        case MODE.SUBJECT:
          subjectLink.title = subjTitle;
          links.push(subjectLink);
          break;
        case MODE.LEVEL:
          levelLink.title = lvlTitle;
          levelLink.disabled = true;
          links.push(levelLink);
          break;
      } 
    } catch (error) {
      console.warn(error);
    }
    return links;
  });

  const getMainTitle = computed(() => {
    if (!getStatesForBooks.value) {
      return '';
    }
    let { subjTitle, lvlTitle } = getStatesForBooks.value;
    let title = '';
    switch (currentMode.value) {
      case MODE.BOTH:
        title = `${subjTitle}, ${lvlTitle}`;
        break;
      case MODE.SUBJECT:
        title = `${subjTitle}`
        break;
      case MODE.LEVEL:
        title = `${lvlTitle}`;
        break;
      default:
        console.warn('There is no title for current mode');
    }
    return title ? 'ГДЗ ' + title : title;
  });

  const filterBooksByRank = computed(() => {
    if (currentMode.value === MODE.BOTH) {
      const emptyBooks = [];
      const filledBooks = [];
      bookEntities.value[-1]?.books?.forEach(b => {
        // This is manager requirement
        if (b.rank === 0 || b.rank === 20) {
          emptyBooks.push(b);
        } else {
          filledBooks.push(b);
        }
      });
      return { emptyBooks, filledBooks };
    } else {
      return bookEntities;
    }
  });


  // Methods
  function initMode() {
    subject.value = getSubjectsToIds.value[subjectName.value] || null;
    level.value = getLevelsToIds.value[levelName.value] || null;
    if (!(subject.value || level.value)) {
      throw new UrlError('Subject and level both undefined.');
    }    
  }

  async function prepareComponent() {
    try {
      bookEntitiesRefs.value = {};
      initMode();
      if (currentMode.value 
        && currentMode.value !== MODE.UNDEFINED
        && currentMode.value !== MODE.LEVEL
      ) {
        const linkObj = { name: "course", query: {} };
        if (subject.value) {
          linkObj.query.subject = subject.value;
        }
        if (level.value) {
          linkObj.query.level = level.value;
        }
        const link = router.resolve(linkObj);
        await useRemoteTitleDescription(link.fullPath);
      }
      await loadBooks();
    } catch (error) {
      console.warn(error);
      if (error instanceof UrlError) {
        router.push({ name: '404' });
      }
    }
  }

  async function loadBooks() {
    if (currentMode.value === MODE.UNDEFINED) {
      console.warn('Both level and subject are undefined');
      // throw new Error('Both level and subject are undefined')
    }
    isLoading.value = true;
    books.value.length = 0;
    try {
      const taskreaderService = new TaskReaderClient(SERVICE_URL, null, null);
      const request = new proto.kazatel.books.BooksRequest();

      const levelsList = level.value 
        ? [level.value] 
        : getGrades.value.map(lvl => lvl.id);
      const subjectList = subject.value 
        ? [subject.value]
        : getCourses.value.map(c => c.id);

      request.setLevelsList(levelsList);
      request.setSubjectsList(subjectList);
      request.setStatusList([7]);
      request.setPageSize(10);
      request.setPageToken("sdfsdfsdf");
      request.setOrderBy("sdfsdfsdf");
      const tokens = extractTokenInfo();
      const metadata = { 'token': tokens.accessToken.token };

      const stream = taskreaderService.books(request, metadata);
      const streamData = await fetchStreamData(stream);
      handleBookStreamData(streamData);
    } catch (err) {
      console.error('ERROR [BOOK LIST]:', err);
    } finally {
      isLoading.value = false;
    }
  }

  function handleBookStreamData(stream) {
    // This is extremely important step
    prepareBooksContainer();
    if (!Object.keys(bookEntities.value).length) {
      console.error('books container preparation failed');
      return;
    }

    // Extract book data and place it in right place
    stream.forEach(res => {
      const book = {
        id: res.getId(),
        bookTitle: res.getName(),
        bookImg: res.getViewUrl(),
        authors: res.getAuthors().split(',').map(
          (a, i) => ({ id: 1 + i, name: a.trim() })
        ),
        bookDescription: {
          isPremium: res.getAttrPremium(),
          year: res.getPublishYear(),
          type: res.getType(),
          part: res.getPart(),
        },
        rank: +res.getRank(),
      };

      let entityIds = [];
      if (currentMode.value === MODE.BOTH) {
        const ids = Object.keys(bookEntities.value);
        if (ids.length === 1) {
          entityIds.push(ids[0]);
        } else {
          console.warn('In BOTH mode book entities must contain only and only 1 key. Something goes wrong.');
          entityIds = [-1];
        }
      } else if (currentMode.value === MODE.SUBJECT) {
        entityIds = res.getLevelsList();
      } else if (currentMode.value === MODE.LEVEL) {
        entityIds.push(res.getSubjectId());
      } else {
        console.warn('Entity ids can not be defined');
      }

      entityIds.forEach(id => {
        try {
          let limit = GENERAL_LIMIT;
          if (currentMode.value === MODE.SUBJECT) {
            limit = SUBJECT_LIMIT;
          } else if (currentMode.value === MODE.LEVEL) {
            limit = LEVEL_LIMIT;
          }
          if (bookEntities.value[id].books.length < limit) {
            bookEntities.value[id].books.push(book);
          }
        } catch (error) {
          console.warn(`Entity ID [${id}] does not exist:`, error);
        }
      });
    });
  }

  function prepareBooksContainer() {
    // Fill bookEntities with template data
    const fictiveId = -1;
    bookEntities.value = {};
    switch (currentMode.value) {
      case MODE.BOTH:
      bookEntities.value[fictiveId] = { title: null, books: [] };
        break;
      case MODE.SUBJECT:
      case MODE.LEVEL:
        let entities = null;
        if (currentMode.value === MODE.SUBJECT) {
          entities = getGrades.value;
        } else {
          entities = getCourses.value;
        }
        entities.forEach(entity => {
          bookEntities.value[entity.id] = { title: entity.title, books: [] };
        });
        break;
      default:
        console.warn('Unsupported mode:', currentMode.value);
    }
    console.log('Book entities has been prepared');
  }

  function fillEntitiesRefs(id, el) {
    if (!bookEntitiesRefs.value.hasOwnProperty(id)) {
      bookEntitiesRefs.value[id] = el;
      observeBookEntities(id, el);
    }
  }

  function getBookTitle(entityId) {
    if (!getStatesForBooks.value) {
      return;
    }
    let { subjTitle, lvlTitle } = getStatesForBooks.value;
    switch (currentMode.value) {
      case MODE.SUBJECT:
        lvlTitle = bookEntities.value[entityId].title;
        break;
      case MODE.LEVEL:
        subjTitle = bookEntities.value[entityId].title;
        break;
    }
    return `${subjTitle}, ${lvlTitle}`;
  }

  function openMoneyManual(event) {
    try {
      openPublicFile('/manual-money.pdf', '_blank');
    } catch (error) {
      console.log(error);
    }
  }

  function toggleEmptyBookList(totalNumber) {
    if (emptyBooksNumber.value === totalNumber) {
      emptyBooksNumber.value = emptyBooksNumberLimit.value;
    } else if (emptyBooksNumber.value === emptyBooksNumberLimit.value) {
      emptyBooksNumber.value = totalNumber;
    }
  }

  function updateScreenWidth() {
    screenWidth.value = window.innerWidth;
    setEmptyBooksLimit(screenWidth.value);
  }

  function setEmptyBooksLimit(screenWidth) {
    emptyBooksNumberLimit.value = EMPTY_BOOKS_LIMIT;
    if (screenWidth < 568) {
      emptyBooksNumberLimit.value = EMPTY_BOOKS_LIMIT_NARROW;
    } else {
      const container = contentContainer.value?.$el || null;
      if (container) {
        const gap = screenWidth < 992 ? 8 : 16;
        const hPadding = screenWidth < 992 ? 16 : 72;
        const bookCardsDense = Math.floor(container.clientWidth  / BOOK_CARD_WIDTH);
        const totalGap = Math.max(bookCardsDense - 1, 0) * gap + hPadding;
        emptyBooksNumberLimit.value = Math.min(
          Math.floor((container.clientWidth - totalGap) / BOOK_CARD_WIDTH), 
          EMPTY_BOOKS_LIMIT
        );
      }
    }
    emptyBooksNumber.value = emptyBooksNumberLimit.value;
  }

  function gotoContentEntity(id) {
    selectedEntityIds.value = new Set([id]);
    const { height } = contentHeader.value.getBoundingClientRect();
    try {
      // Vue stores dynamic `ref` as an array
      const contentEntity = bookEntitiesRefs.value[id];
      contentEntity.style = `scroll-margin-top: ${height + 24}px`;
      contentEntity.scrollIntoView({ behavior: 'smooth' });
    } catch (error) {
      console.error(error);
    }
  }

  function goToAllBooks(id) {
    const params = { subject: null, level: null };
    const name = 'subject-level-books';
    switch (currentMode.value) {
      case MODE.SUBJECT:
        params.level = getIdsToLevels.value[id];
        params.subject = subjectName.value;
        break;
      case MODE.LEVEL:
        params.subject = getIdsToSubjects.value[id];
        params.level = levelName.value;
        break;
    }
    console.log('ROUTE:', name, params);
    if (name) {
      router.push({ name, params });
    }
  }


  // Life cycles
  onMounted(() => {
    window.addEventListener('resize', updateScreenWidth);
    setEmptyBooksLimit(window.innerWidth);
    if (contentContainer.value) {
      contentContainer.value?.$el.scrollIntoView();
    }
  });

  onUnmounted(() => {
    window.removeEventListener('resize', updateScreenWidth);
  });


  return {
    MODE,
    LOADING_STATES,
    contentHeader,
    observeHeader,
    entityIntersections,
    observeBookEntities,

    // data
    currentMode,
    subjectName,
    levelName,
    subject,
    level,
    isLoading,
    books,
    bookEntities,
    bookEntitiesRefs,
    selectedEntityIds,
    screenWidth,
    emptyBooksNumber,
    emptyBooksNumberLimit,

    // computed
    loadingStatesFull,
    updateBreadcrumbLinks,
    getMainTitle,
    filterBooksByRank,

    // methods
    prepareComponent,
    fillEntitiesRefs,
    getBookTitle,
    openMoneyManual,
    toggleEmptyBookList,
    gotoContentEntity,
    goToAllBooks,
  }
}


export {
  MODE,
}